import { ArrowUpRight, ArrowDownRight, Truck, Container } from "lucide-react";
import { useViewerTimezone } from "@/hooks/useViewerTimezone";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { OrderStatusBadge } from "@/components/ui/status-badge";
import { OrderWithRelations } from "@/components/schedule/types";
import { formatTimeWithTz } from "@/lib/order-time";
import type { Location } from "@/types/tms";
import { PlaceholderChip } from "./PlaceholderChip";

interface Props {
  order: OrderWithRelations;
  locations?: Location[];
  legSequence?: number | null;
  isMultiLeg?: boolean;
  onClick: () => void;
}

export function AssignmentListCard({ order, locations, legSequence, isMultiLeg, onClick }: Props) {
  const viewerTz = useViewerTimezone();
  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className="cursor-pointer transition-colors hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-bold text-base text-foreground">{order.unique_id}</div>
          {isMultiLeg && legSequence != null && (
            <Badge className="bg-primary text-primary-foreground hover:bg-primary">
              Stop {legSequence}
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <OrderStatusBadge status={order.status} />
          <PlaceholderChip label="Truck ID" value={order.assigned_tractor?.name} icon={Truck} />
          <PlaceholderChip label="Trailer ID" value={order.trailer_id} icon={Container} />
        </div>

        <div className="grid grid-cols-2 gap-3 pt-1">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 font-semibold text-foreground truncate">
              <ArrowUpRight className="h-4 w-4 shrink-0" />
              <span className="truncate">{order.ship_from_city || "—"}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {formatTimeWithTz(order.ready_time, viewerTz, "MMM d, h:mm a")}
            </div>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 font-semibold text-foreground truncate">
              <ArrowDownRight className="h-4 w-4 shrink-0" />
              <span className="truncate">{order.ship_to_city || "—"}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {formatTimeWithTz(order.requested_delivery_time, viewerTz, "MMM d, h:mm a")}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}